import { v4 as uuid } from 'uuid';
import { IRateioAdapter } from 'Data/interfaces/Faturamento/IRateioPort';
import {
  BillingModeEnum,
  IBillingSplitResponse,
  IRateioResponse,
  IRateioShareResponse,
  IRateioUpdateRequest,
  RateioModeEnum,
} from 'Data/interfaces/Faturamento/IRateio';
import { ISpeRequest, ISpeResponse } from 'Data/interfaces/Faturamento/ISpe';
import { distributeUniformWeights } from 'Utils/RateioUtils';
import {
  ITermsAcceptanceResponse,
  TermsAcceptanceStatusEnum,
} from 'Data/interfaces/Faturamento/ITermsAcceptance';

// Adaptador em memória. Existe porque o backend de SPEs ainda não foi construído
// (épico 12253) — permite desenvolver e revisar as telas antes dele. Não persiste:
// o estado se perde ao recarregar a página.
//
// Ao ligar o backend, trocar a factory em ./index.ts. Nenhum componente muda.
//
// O mock só conhece um tenant: ele ignora o `tenantId` opcional da porta, então o
// painel /adm enxerga e altera o mesmo estado da tela do cliente — o que ajuda a
// revisar as duas telas juntas.

// Latência artificial para que a UI exiba estados de loading de verdade durante o
// desenvolvimento. Zerada em teste: sem isso, uma suíte que faz dezenas de chamadas
// estoura o timeout do jest sem que haja nada errado com o código.
const LATENCY_MS = process.env.NODE_ENV === 'test' ? 0 : 350;

const delay = <T>(value: T): Promise<T> =>
  new Promise((resolve) => setTimeout(() => resolve(value), LATENCY_MS));

const seedSpes = (): ISpeResponse[] => [
  {
    SpeId: uuid(),
    CNPJ: '12.345.678/0001-90',
    CompanyName: 'SPE Residencial Aurora',
    Phone: '(62) 3251-4401',
    Email: 'financeiro@aurora.com.br',
    Zip: '74810-100',
    StreetAddress: 'Av. Deputado Jamel Cecílio',
    Number: 2929,
    District: 'Jardim Goiás',
    City: 'Goiânia',
    State: 'GO',
    LegalRepresentative: {
      Name: 'Ana Beatriz Costa',
      CPF: '123.456.789-09',
      Role: 'Sócia-administradora',
      Email: 'ana.costa@aurora.com.br',
      Phone: '(62) 99811-2200',
    },
  },
  {
    SpeId: uuid(),
    CNPJ: '23.456.789/0001-01',
    CompanyName: 'SPE Parque das Águas',
    Phone: '(62) 3251-4402',
    Email: 'financeiro@parqueaguas.com.br',
    Zip: '74884-100',
    StreetAddress: 'Rua T-55',
    Number: 810,
    District: 'Setor Bueno',
    City: 'Goiânia',
    State: 'GO',
    LegalRepresentative: {
      Name: 'Rafael Nunes Teixeira',
      CPF: '987.654.321-00',
      Role: 'Diretor financeiro',
      Email: 'rafael.teixeira@parqueaguas.com.br',
      Phone: '(62) 99744-1180',
    },
  },
  {
    SpeId: uuid(),
    CNPJ: '34.567.890/0001-12',
    CompanyName: 'SPE Vila Nova Horizonte',
    Phone: '(62) 3251-4403',
    Email: 'financeiro@vilanovahorizonte.com.br',
    Zip: '74175-120',
    StreetAddress: 'Av. Portugal',
    Number: 1148,
    District: 'Setor Marista',
    City: 'Goiânia',
    State: 'GO',
    LegalRepresentative: {
      Name: 'Camila Andrade Rocha',
      CPF: '456.789.123-70',
      Role: 'Sócia-administradora',
      Email: 'camila.rocha@vilanovahorizonte.com.br',
      Phone: '(62) 99655-4410',
    },
  },
];

// A regra de arredondamento mora em Utils/RateioUtils para não existir em dois
// lugares — a mesma função alimenta a UI e este mock.
const distributeUniform = (speIds: string[]): IRateioShareResponse[] =>
  distributeUniformWeights(speIds.length).map((share, index) => ({
    SpeId: speIds[index],
    Weight: share.weight,
    IsRoundingAdjusted: share.isRoundingAdjusted,
  }));

export class RateioMockAdapter implements IRateioAdapter {
  private billingMode: BillingModeEnum = BillingModeEnum.SingleCompany;
  private spes: ISpeResponse[] = seedSpes();
  private rateioMode: RateioModeEnum = RateioModeEnum.Uniform;
  private shares: IRateioShareResponse[] = [];
  // Ciência nos Termos simulada: tudo começa pendente; o reenvio só renova a data.
  private terms: Record<string, ITermsAcceptanceResponse> = {};

  constructor() {
    this.shares = distributeUniform(this.spes.map((spe) => spe.SpeId));
  }

  async getBillingSplit(): Promise<IBillingSplitResponse> {
    return delay(this.snapshot());
  }

  async setBillingMode(mode: BillingModeEnum): Promise<IBillingSplitResponse> {
    this.billingMode = mode;
    if (mode === BillingModeEnum.SpeSplit && this.shares.length === 0) {
      this.shares = distributeUniform(this.spes.map((spe) => spe.SpeId));
    }
    return delay(this.snapshot());
  }

  async addSpe(spe: ISpeRequest): Promise<ISpeResponse> {
    const created: ISpeResponse = {
      SpeId: uuid(),
      CNPJ: spe.cnpj,
      CompanyName: spe.companyName,
      Phone: spe.phone,
      Email: spe.email,
      OwnerName: spe.ownerName,
      Site: spe.site,
      Zip: spe.zip,
      StreetAddress: spe.streetAddress,
      Number: spe.number,
      District: spe.district,
      City: spe.city,
      State: spe.state,
      Complement: spe.complement,
      LegalRepresentative: {
        Name: spe.legalRepresentative.name,
        CPF: spe.legalRepresentative.cpf,
        Role: spe.legalRepresentative.role,
        Email: spe.legalRepresentative.email,
        Phone: spe.legalRepresentative.phone,
      },
    };

    this.spes = [...this.spes, created];

    if (this.rateioMode === RateioModeEnum.Uniform) {
      this.shares = distributeUniform(this.spes.map((item) => item.SpeId));
    } else {
      // Entra com peso zero: roubar percentual das outras mexeria em valores que
      // o admin definiu de propósito.
      this.shares = [
        ...this.shares,
        { SpeId: created.SpeId, Weight: 0, IsRoundingAdjusted: false },
      ];
    }

    return delay({ ...created });
  }

  async updateSpe(speId: string, spe: ISpeRequest): Promise<ISpeResponse> {
    const current = this.spes.find((item) => item.SpeId === speId);

    if (!current) {
      throw new Error(`SPE não encontrada: ${speId}`);
    }

    const updated: ISpeResponse = {
      ...current,
      CNPJ: spe.cnpj,
      CompanyName: spe.companyName,
      Phone: spe.phone,
      Email: spe.email,
      OwnerName: spe.ownerName,
      Site: spe.site,
      Zip: spe.zip,
      StreetAddress: spe.streetAddress,
      Number: spe.number,
      District: spe.district,
      City: spe.city,
      State: spe.state,
      Complement: spe.complement,
      LegalRepresentative: {
        Name: spe.legalRepresentative.name,
        CPF: spe.legalRepresentative.cpf,
        Role: spe.legalRepresentative.role,
        Email: spe.legalRepresentative.email,
        Phone: spe.legalRepresentative.phone,
      },
    };

    this.spes = this.spes.map((item) => (item.SpeId === speId ? updated : item));

    return delay({ ...updated });
  }

  async removeSpe(speId: string): Promise<IBillingSplitResponse> {
    this.spes = this.spes.filter((spe) => spe.SpeId !== speId);
    this.reflowAfterSpeChange();
    return delay(this.snapshot());
  }

  async updateRateio(request: IRateioUpdateRequest): Promise<IRateioResponse> {
    this.rateioMode = request.mode;

    if (request.mode === RateioModeEnum.Uniform) {
      this.shares = distributeUniform(this.spes.map((spe) => spe.SpeId));
    } else {
      this.shares = request.shares.map((share) => ({
        SpeId: share.speId,
        Weight: share.weight,
        IsRoundingAdjusted: false,
      }));
    }

    return delay({ Mode: this.rateioMode, Shares: [...this.shares] });
  }

  async resendTermsAcceptance(speId?: string): Promise<void> {
    const now = new Date().toISOString();
    this.terms[speId ?? 'headquarters'] = {
      ...this.pendingTerms(),
      RequestedAtUtc: now,
      EmailSentAtUtc: now,
    };
    return delay(undefined);
  }

  // Criado uma vez por empresa e guardado: gerar a cada leitura mudaria a data e
  // duas leituras do mesmo estado deixariam de ser iguais.
  private termsFor(key: string): ITermsAcceptanceResponse {
    if (!this.terms[key]) {
      this.terms[key] = this.pendingTerms();
    }
    return { ...this.terms[key] };
  }

  private pendingTerms(): ITermsAcceptanceResponse {
    return {
      Status: TermsAcceptanceStatusEnum.Pending,
      IsExpired: false,
      RecipientName: 'Representante (simulado)',
      RecipientEmail: 'representante@exemplo.com.br',
      RequestedAtUtc: new Date().toISOString(),
      EmailSentAtUtc: null,
    };
  }

  private snapshot(): IBillingSplitResponse {
    const isSplit = this.billingMode === BillingModeEnum.SpeSplit;
    return {
      BillingMode: this.billingMode,
      Rateio: isSplit ? { Mode: this.rateioMode, Shares: [...this.shares] } : null,
      Spes: this.spes.map((spe) => ({
        ...spe,
        TermsAcceptance: this.termsFor(spe.SpeId),
      })),
      HeadquartersTermsAcceptance: isSplit ? this.termsFor('headquarters') : null,
    };
  }

  private reflowAfterSpeChange(): void {
    if (this.rateioMode === RateioModeEnum.Uniform) {
      this.shares = distributeUniform(this.spes.map((spe) => spe.SpeId));
      return;
    }
    // Em Custom, remover uma SPE deixa a soma abaixo de 100 de propósito: quem
    // decide para onde vai o percentual órfão é o admin do tenant, não o sistema.
    this.shares = this.shares.filter((share) =>
      this.spes.some((spe) => spe.SpeId === share.SpeId)
    );
  }
}
